/* eslint-disable @typescript-eslint/no-explicit-any */
import TableHeaderWithTab from "@/components/common/table/header-with-tab";
import Pagination from "@/components/common/table/pagination";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { useAuth } from "@/contexts/AuthProvider";
import { useAuthorization } from "@/hooks/use-authorization";
import { useGetFormalityRequests } from "@/queries/formaly-request";
import { useGetMe } from "@/queries/me";
import { useDocumentMetadataStore } from "@/stores/documents/useDocumentMetadataStore";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import {
  formatDateTime,
  getFormattedElapsedDaysWithColor,
} from "@/utils/dates";
import { Permissions } from "@/utils/permissions";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { RequestAssigneeModal } from "../../components/ui/RequestAssigneeModal";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import RequestStatus from "./request-state";
import type { RefName } from "@/workflow_api/types/workflow";
import {
  buildHierarchyStatusFilter,
  buildHierarchyStatusString,
} from "./utils/serialization";
import { useMetricsPermissions } from "./hooks/use-metrics-perms";

const columnHelper = createColumnHelper<FormalityRequestType>();

// eslint-disable-next-line react-refresh/only-export-components
export const FRSTATUS_BADGES = {
  pending: {
    variant: "warning",
    label: "Pendiente",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },
  draft: {
    variant: "neutral",
    label: "Borrador",
    icon: (
      <Icon type="far" name="file" className="text-neutral-quiet text-xs" />
    ),
  },
  sent: {
    variant: "info",
    label: "Recibido",
    icon: (
      <Icon type="far" name="envelope" className="text-info-quiet text-xs" />
    ),
  },
  in_process: {
    variant: "action",
    label: "En proceso",
    icon: (
      <Icon
        type="far"
        name="arrows-rotate"
        className="text-action-quiet text-xs"
      />
    ),
  },
  in_correction: {
    variant: "action",
    label: "En revisión",
    icon: (
      <Icon type="far" name="search" className="text-action-quiet text-xs" />
    ),
  },
  in_leader_review: {
    variant: "warning",
    label: "Para visto bueno",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },
  with_corrections: {
    variant: "warning",
    label: "Observado",
    icon: (
      <Icon
        type="far"
        name="file-edit"
        className="text-warning-quiet text-xs"
      />
    ),
  },
  under_review: {
    variant: "warning",
    label: "Revisando corrección",
    icon: (
      <Icon
        type="far"
        name="file-edit"
        className="text-warning-quiet text-xs"
      />
    ),
  },
  extension_request: {
    variant: "warning",
    label: "Solicitud de prórroga",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },
  suspension_request: {
    variant: "warning",
    label: "Solicitud suspensión",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },
  on_extension: {
    variant: "warning",
    label: "En prórroga",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },
  with_suspension: {
    variant: "neutral",
    label: "Suspendido",
    icon: (
      <Icon type="far" name="pause" className="text-neutral-quiet text-xs" />
    ),
  },
  payment_pending: {
    variant: "info",
    label: "Pendiente de pago",
    icon: <Icon type="far" name="dollar" className="text-info-quiet text-xs" />,
  },
  approved: {
    variant: "success",
    label: "Aprobado",
    icon: (
      <Icon type="far" name="check" className="text-success-quiet text-xs" />
    ),
  },
  rejected: {
    variant: "danger",
    label: "Rechazado",
    icon: (
      <Icon type="far" name="times" className="text-danger-quiet text-xs" />
    ),
  },
  unfair: {
    variant: "neutral",
    label: "Improcedente",
    icon: <Icon type="far" name="ban" className="text-neutral-quiet text-xs" />,
  },
  canceled: {
    variant: "neutral",
    label: "Cancelado",
    icon: <Icon type="far" name="ban" className="text-neutral-quiet text-xs" />,
  },

  //no se que hacer con ellos
  time_expired: {
    variant: "danger",
    label: "Tiempo expirado",
    icon: (
      <Icon type="far" name="clock" className="text-danger-quiet text-xs" />
    ),
  },
  can_extended: {
    variant: "warning",
    label: "Prórroga disponible",
    icon: (
      <Icon type="far" name="clock" className="text-warning-quiet text-xs" />
    ),
  },

  done: {
    variant: "success",
    label: "Resuelto",
    icon: (
      <Icon type="far" name="check" className="text-success-quiet text-xs" />
    ),
  },

  force_completed: {
    variant: "success",
    label: "Finalizado forzosamente",
    icon: (
      <Icon type="far" name="check" className="text-success-quiet text-xs" />
    ),
  },
};

const createColumns = (
  hasActivityBlock: boolean,
  setModalState: (state: "idle" | "filters" | "userSelection") => void,
  onUpdateAssignee: (request: FormalityRequestType) => void,
  shouldShowAssignedColumn = false,
  groupsImLeaderOf: string[] = [],
  setGroupsIdToFind: (groupsId: string[]) => void,
  onNavigate: (id: string) => void,
) =>
  [
    columnHelper.accessor("process.name", {
      id: "process.name",
      header: "Nombre del trámite",
      sortUndefined: "last",
      meta: {
        className:
          "max-w-[80px] sm:max-w-[250px] text-ellipsis overflow-hidden",
        cellClassName: "group",
      },
      cell: (info) => {
        if (!info.getValue()) return <p>&nbsp;</p>;
        return (
          <TableLeadingCell
            title={info.getValue()}
            showButton={true}
            onButtonClick={() => {
              const id = info?.row?.original?.id;
              if (!id) return;
              onNavigate(id);
            }}
          />
        );
      },
    }),

    columnHelper.accessor("code", {
      header: "Número ID",
      sortUndefined: "last",
      meta: {
        className:
          "hidden min-[1500px]:table-cell text-ellipsis overflow-hidden",
      },
      cell: (info) => {
        if (!info.getValue()) return <p>&nbsp;</p>;
        return info.getValue().toUpperCase();
      },
    }),

    columnHelper.accessor("status", {
      header: "Estado",
      sortUndefined: "last",
      meta: {
        className: "hidden min-[1400px]:table-cell",
      },
      cell: (info) => {
        return (
          <RequestStatus
            node_name={
              info.row.original.current_node?.display_name || "Desconocido"
            }
            node_type={
              (info.row.original.current_node?.ref_name as RefName) ||
              "resolution-node"
            }
            status={info.row.original.status || "cancelled"}
            status_label={info.row.original.status_label || ""}
          />
        );
      },
    }),

    columnHelper.accessor((row) => row?.assignedTo?.name, {
      header: "Asignado",
      id: "groups",
      sortUndefined: "last",
      enableSorting: false,
      meta: {
        className: `hidden ${hasActivityBlock ? "lg:table-cell" : " 3xl:table-cell"} `,
      },
      cell: (info) => {
        const groups = info.row.original.groups;
        const assignedTo = info.row.original.assignedTo;

        if (groups.length === 0) {
          if (!assignedTo?.name) return <p>&nbsp;</p>;

          const initials = assignedTo.name.split(" ")[0][0];

          return (
            <div className="flex items-center gap-4">
              <Avatar size="xs">
                <AvatarFallback color="action">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0">
                <span className="text-body font-normal">
                  {`${assignedTo?.name || ""} ${assignedTo?.last_name || ""}`}
                </span>
                <span className="text-body-quiet font-normal text-xs">
                  {assignedTo?.position?.name || ""}
                </span>
              </div>
            </div>
          );
        }

        if (groups.length === 1) {
          const groupAssignedUser = assignedTo || groups[0].pivot.assigned_user;
          const initials = groupAssignedUser?.name.split(" ")[0][0];

          return (
            <div className="flex items-center gap-4">
              <Avatar size="xs">
                <AvatarFallback color="action">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-0">
                <span className="text-body font-normal">
                  {`${groupAssignedUser?.name || ""} ${groupAssignedUser?.last_name || ""}`}
                </span>
                <span className="text-body-quiet font-normal text-xs">
                  {groupAssignedUser?.position?.name || ""}
                </span>
              </div>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-0">
              <span className="text-body font-normal">Múltiples asignados</span>
              <span className="text-body-quiet font-normal text-xs">
                {groups.length} usuarios asignados
              </span>
            </div>
          </div>
        );
      },
    }),

    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Fecha de ingreso",
      meta: {
        className: `hidden ${hasActivityBlock ? "lg:table-cell" : "3xl:table-cell"} `,
      },
      sortUndefined: "last",
      cell: (info) => {
        return formatDateTime(info?.getValue() || "");
      },
    }),

    columnHelper.accessor("first_assigned_at", {
      id: "elapsed_days",
      header: "Tiempo transcurrido",
      meta: {
        className: "hidden md:table-cell",
      },
      sortUndefined: "first",
      cell: (row) => {
        const creationDate = row.row?.original?.first_assigned_at;
        if (!creationDate) return <div>-</div>;

        const { formatted, colorClass } =
          getFormattedElapsedDaysWithColor(creationDate);

        const status = row.row?.original?.status?.toLowerCase();
        const shouldShowBadge =
          status === "can_extended" || status === "time_expired";
        const badgeColor = status === "can_extended" ? "warning" : "danger";

        return (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon
                type="far"
                name="calendar"
                className={`ml-1 mr-4 ${colorClass}-quiet`}
                size="lg"
              />
              <span className={`${colorClass}`}>{formatted}</span>
            </div>
            {shouldShowBadge && (
              <Avatar size="xs" className="mr-2">
                <AvatarFallback
                  color={badgeColor}
                  className="flex items-center justify-center"
                >
                  <Icon
                    type="fas"
                    name="clock"
                    className={`text-${badgeColor}-quiet`}
                    size="sm"
                  />
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        );
      },
    }),

    columnHelper.display({
      id: "actions",
      header: "",
      enableSorting: false,
      meta: {
        className: "w-[56px]",
      },
      cell: (row) => {
        return (
          <div className="w-full flex justify-end">
            <Menu
              entity="gestión"
              id={row.row?.original?.id?.toString() || "tramite"}
              groups={row.row?.original?.groups ?? []}
              groupsImLeaderOf={groupsImLeaderOf}
              onUpdateAssignee={(groupsProcessImLeaderOf: string[]) => {
                setGroupsIdToFind(groupsProcessImLeaderOf);
                onUpdateAssignee(row.row?.original);
                setModalState("userSelection");
              }}
            />
          </div>
        );
      },
    }),
  ].filter(({ id }) => {
    if (id === "assignedTo.name" && !shouldShowAssignedColumn) return false;
    return true;
  }) as ColumnDef<FormalityRequestType>[];

const Menu = ({
  entity,
  id,
  groups,
  groupsImLeaderOf = [],
  onUpdateAssignee,
}: {
  entity: string;
  id: string;
  groups: any[];
  onUpdateAssignee: (groupsImLeaderOf: string[]) => void;
  groupsImLeaderOf: string[];
}) => {
  const { hasPermission } = useAuthorization();
  const canReadRequests = hasPermission(Permissions.formality_requests.read);
  const canUpdateAssignee = hasPermission(
    Permissions.formality_requests.assign,
  );

  const groupsProcessImLeaderOf = useMemo(() => {
    return groups
      .filter((group) => groupsImLeaderOf.includes(group.id))
      .map((g) => g.id);
  }, [groups, groupsImLeaderOf]);

  if (!canReadRequests && !canUpdateAssignee) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">
            Acciones para {entity} {id}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-55.5 min-w-56 rounded-lg border-neutral-silent text-body font-normal"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {canReadRequests && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink to={`/tramite/${id}`} className="w-full h-full block">
                Detalle
              </NavLink>
            </DropdownMenuItem>
          )}
          {canUpdateAssignee && groupsProcessImLeaderOf.length > 0 && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onUpdateAssignee(groupsProcessImLeaderOf)}
            >
              Reasignar solicitud
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RequestManagementTable = () => {
  const { data: me } = useGetMe();
  const { getUserRoleType } = useAuth();
  const userRoleType = getUserRoleType();
  const navigate = useNavigate();
  const { hasActivityBlock } = useMetricsPermissions();

  const isUserLeaderInSomeGroup = useMemo(() => {
    if (!me) return false;
    return me.data.groups.some(
      (group) => group.pivot.seniority === 1 || group.pivot.seniority === 2,
    );
  }, [me]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [page, setPage] = useQueryState("page", parseAsInteger);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!updated_at"),
  );
  const { hasPermission } = useAuthorization();
  const hasSubmissions = hasPermission(Permissions.widgets.submissions);
  const canReadRequests = hasPermission(Permissions.formality_requests.read);
  const [groupsIdToFind, setGroupsIdToFind] = useState<string[]>([]);
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault(""),
  );
  const [suspensionStatus, setSuspensionStatus] = useQueryState(
    "suspensionStatus",
    parseAsString.withDefault(""),
  );
  const [created_at, setCreatedAt] = useQueryState(
    "created_at",
    parseAsString.withDefault(""),
  );

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>(
    defaultFilters({
      status,
      created_at,
      suspensionStatus,
    }),
  );

  useEffect(() => {
    setFormatFilters(
      defaultFilters({
        status,
        created_at,
        suspensionStatus,
      }),
    );
  }, [status, created_at, suspensionStatus]);

  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, setPage]);

  const { data, isLoading } = useGetFormalityRequests(
    {
      ...(deferredQuery ? { "filter[search]": deferredQuery } : {}),
      page: page || 1,
      per_page: 20,
      ...(order_by ? { order_by: order_by } : {}),
      ...(status ? { "filter[node_status_pairs]": status } : {}),
      ...(created_at ? { "filter[created_at]": created_at } : {}),
      ...(me?.data?.id && activeTab === "my-signatures"
        ? { "filter[signable_by]": me.data.id.toString() }
        : {}),
      ...(suspensionStatus
        ? { "filter[suspension_status]": suspensionStatus }
        : {}),
      ...(activeTab === "express"
        ? { "filter[is_express]": "1" }
        : { "filter[is_express]": "0" }),
      ...(userRoleType !== "admin" && {
        ...(isUserLeaderInSomeGroup && {
          group: me?.data.groups
            .filter((group) => group.pivot.seniority === 1)
            .map((group) => group.id)
            .join(","),
        }),
        ...(!isUserLeaderInSomeGroup && {
          assigned: me?.data.id.toString(),
        }),
      }),
    },
    5000,
    !!me?.data && canReadRequests,
  );

  const groupsImLeaderOf = useMemo(() => {
    if (!me?.data?.groups?.length) return [];

    const leaderGroups = me.data.groups.filter(
      (group) => group?.pivot?.seniority === 1 || group?.pivot?.seniority === 2,
    );

    if (leaderGroups.length === 0) return [];

    return leaderGroups.map((g) => g.id);
  }, [me?.data?.groups]);

  const [modalState, setModalState] = useState<
    "idle" | "filters" | "userSelection"
  >("idle");

  const [selectedRequest, setSelectedRequest] =
    useState<FormalityRequestType | null>(null);

  const { setDocuments } = useDocumentMetadataStore();

  // Extract and store document metadata when data is fetched
  useEffect(() => {
    if (!data) return;

    // Handle both possible response structures: { data: [...] } or direct array
    const dataArray = Array.isArray(data) ? data : data?.data || [];

    if (Array.isArray(dataArray) && dataArray.length > 0) {
      const allDocuments = dataArray
        .filter((request: FormalityRequestType) => request.documents)
        .flatMap((request: FormalityRequestType) =>
          (request.documents || []).map((doc) => ({
            ...doc,
            node_id: doc.node_id,
            field_id: doc.field_id,
            field_name: doc.field_name,
            field_label: doc.field_label,
          })),
        );
      setDocuments(allDocuments);
    }
  }, [data, setDocuments]);

  const columns = createColumns(
    hasActivityBlock,
    setModalState,
    (request: FormalityRequestType) => {
      setSelectedRequest(request);
    },
    userRoleType === "admin" || isUserLeaderInSomeGroup, // Show assigned column only for admin and group leaders<
    groupsImLeaderOf,
    setGroupsIdToFind,
    (id: string) => navigate(`/tramite/${id}`),
  );

  // All filtering is now handled server-side by the API
  const filteredData = useMemo(() => {
    // Check if data exists and has the expected structure
    if (!data) return [];

    // Handle both possible response structures: { data: [...] } or direct array
    const dataArray = Array.isArray(data) ? data : data?.data || [];

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return [];
    }

    return dataArray;
  }, [data]);

  // Calculate pagination - now both tabs use server-side pagination
  const filteredPagination = useMemo(() => {
    if (!data || Array.isArray(data)) {
      return {
        currentPage: 1,
        totalItems: filteredData.length,
        totalPages: 1,
        pageSize: filteredData.length,
        hasNext: false,
        hasPrevious: false,
      };
    }

    // Use server-side pagination for both tabs
    return {
      currentPage: data?.meta?.current_page || 1,
      totalItems: data?.meta?.total || 0,
      totalPages: data?.meta?.last_page || 0,
      pageSize: Array.isArray(data?.data)
        ? data.data.length
        : data?.data?.length || 0,
      hasNext: (data?.meta?.current_page || 1) < (data?.meta?.last_page || 1),
      hasPrevious: (data?.meta?.current_page || 1) > 1,
    };
  }, [filteredData, data]);

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      defaultFilters({
        status: "",
        created_at: "",
      }),
    );
    setStatus("");
    setCreatedAt(null);
    setSuspensionStatus(null);
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(buildHierarchyStatusString(f as any));
        } else if (f.field === "created_at") {
          setCreatedAt(f.result);
        } else if (f.field === "suspensionStatus") {
          setSuspensionStatus(f.result);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const handleCloseUserSelection = () => {
    setModalState("idle");
    setSelectedRequest(null);
    setGroupsIdToFind([]);
  };

  return (
    <div
      data-testid="request-management-table"
      className="bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-custom overflow-hidden"
    >
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
      <TableHeaderWithTab
        tabs={[
          { label: "Normales", value: "all" },
          { label: "Express", value: "express" },
          { label: "Mis firmas", value: "my-signatures" },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        createLabel=""
        search={searchInput}
        placeholder="Buscar solicitud..."
        setSearch={setSearchInput}
        isLoading={isLoading}
        canCreate={false}
        onCreate={() => {
          //navigate("/roles/new");
        }}
        onFilter={() => setModalState("filters")}
        filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
      />
      {hasSubmissions && canReadRequests ? (
        <>
          {filteredData.length > 0 ? (
            <>
              <TableData
                columns={columns}
                data={filteredData}
                sorting={sorting}
                onSortingChange={(val) => {
                  setSorting(val);
                  if (val.length === 0) {
                    setOrderBy("!updated_at");
                    return;
                  }
                  setOrderBy(
                    `${val[0]?.desc ? "!" : ""}${val[0].id === "elapsed_days" ? "created_at" : val[0].id}`,
                  );
                }}
                stripped
                isFirstLoading={!data}
                wrapperClassName="min-h-125"
              />
              <div className="p-3">
                <Pagination
                  currentPage={filteredPagination.currentPage}
                  totalItems={filteredPagination.totalItems}
                  hasNext={filteredPagination.hasNext}
                  hasPrevious={filteredPagination.hasPrevious}
                  onPageChange={(page) => {
                    // Only allow page changes for "all" tab (server-side pagination)
                    if (activeTab === "all") {
                      setPage(page);
                    }
                  }}
                  onNext={() => {
                    // Only allow navigation for "all" tab
                    if (activeTab === "all" && filteredPagination.hasNext) {
                      setPage((data?.meta?.current_page || 1) + 1);
                    }
                  }}
                  onPrevious={() => {
                    // Only allow navigation for "all" tab
                    if (activeTab === "all" && filteredPagination.hasPrevious) {
                      setPage((data?.meta?.current_page || 1) - 1);
                    }
                  }}
                  pageSize={filteredPagination.pageSize}
                  totalPages={filteredPagination.totalPages}
                />
              </div>
            </>
          ) : !isLoading && data ? (
            <div className="relative border-y border-neutral-quiet w-full h-100 overflow-x-auto text-body flex flex-col justify-center items-center bg-neutral-object-accent-silent rounded-b-lg shadow-none">
              <Heading variant="h6" className="text-on-disabled">
                Sin entradas creadas
              </Heading>
              <span className="text-sm text-on-disabled">
                Crea una nueva entrada para poder ver sus detalles aquí.
              </span>
            </div>
          ) : (
            <TableData
              columns={columns}
              data={filteredData}
              sorting={sorting}
              onSortingChange={(val) => {
                setSorting(val);
                if (val.length === 0) {
                  setOrderBy("!updated_at");
                  return;
                }
                setOrderBy(
                  `${val[0]?.desc ? "!" : ""}${val[0].id === "elapsed_days" ? "created_at" : val[0].id}`,
                );
              }}
              stripped
              isFirstLoading={!data}
            />
          )}
          {modalState === "userSelection" && (
            <RequestAssigneeModal
              isOpen={modalState === "userSelection"}
              onClose={handleCloseUserSelection}
              selectedRequest={selectedRequest}
              groupsImLeaderOf={groupsIdToFind}
            />
          )}
        </>
      ) : (
        <div
          data-testid="request-management-table-fallback"
          className="relative border-y border-neutral-quiet w-full h-64 overflow-x-auto text-body flex justify-center items-center gap-4 bg-neutral-object-accent-silent rounded-b-lg shadow-none"
        >
          <span className="text-center font-normal text-on-disabled-quiet">
            Sin permiso para ver estos datos
          </span>
        </div>
      )}
    </div>
  );
};

export default RequestManagementTable;

const defaultFilters: (val: {
  status?: string;
  created_at?: string;
  suspensionStatus?: string;
}) => FilterOption[] = ({
  status = "",
  created_at = "",
  suspensionStatus = "",
}) => [
  {
    id: 1,
    field: "created_at",
    type: filterTypesEnum.dates,
    label: "Última actualización",
    shouldDisabledFutureDates: true,
    isUpdated: created_at !== "",
    result: created_at,
  },
  buildHierarchyStatusFilter(status) as any,
  {
    id: 3,
    field: "suspensionStatus",
    type: filterTypesEnum.status,
    label: "Estado de suspención",
    isUpdated: suspensionStatus !== "",
    result: suspensionStatus,
    status: [
      {
        field: "PENDING",
        label: "Pendiente",
        value: suspensionStatus.includes("PENDING"),
      },
      {
        field: "APPROVED",
        label: "Aprobado",
        value: suspensionStatus.includes("APPROVED"),
      },
      {
        field: "DENIED",
        label: "Denegado",
        value: suspensionStatus.includes("DENIED"),
      },
      {
        field: "SCALED",
        label: "Escalado",
        value: suspensionStatus.includes("SCALED"),
      },
    ],
  },
];
